import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { BusinessCard } from '@/components/BusinessCard';
import { HowItWorksModal } from '@/components/HowItWorksModal';
import { JsonLd, organizationSchema, websiteSchema } from '@/components/JsonLd';
import { PlatformStatsBar } from '@/components/PlatformStatsBar';
import { MobileHomeExperience } from '@/components/mobile/MobileHomeExperience';
// Testimonials section removed — pending real beta-user reviews to replace
import { useI18n } from '@/lib/i18n';
import { useCountry } from '@/lib/countryContext';
import { categoriesAPI, businessesAPI, utilityAPI } from '@/lib/api';
import { useSelectedCity } from '@/lib/selectedCity';
import {
  Search, MapPin, ArrowRight, CheckCircle2,
  Sparkles, Heart, Dumbbell, Flower2, Scale, Briefcase, Car, PawPrint, PartyPopper, HelpCircle, Moon,
  Scissors, Coffee, Music, Camera, Utensils, Palette, Wrench, Stethoscope, GraduationCap, Plane,
  Star, Shield, Clock, Users, CalendarIcon, ChevronDown, Building2, Zap
} from 'lucide-react';

const iconMap = {
  Sparkles, Heart, Dumbbell, Flower2, Scale, Briefcase, Car, PawPrint, PartyPopper, HelpCircle, Moon,
  Scissors, Coffee, Music, Camera, Utensils, Palette, Wrench, Stethoscope, GraduationCap, Plane,
};

export default function HomePage() {
  const { t, language } = useI18n();
  const { countryCode, country } = useCountry();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [featuredBusinesses, setFeaturedBusinesses] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategoryId, setSelectedCategoryId] = useState('');
  // City is persisted globally via localStorage so it survives navigation.
  // Auto-geolocation must NOT override a manual pick.
  const [city, setCity] = useSelectedCity();
  const [serviceOpen, setServiceOpen] = useState(false);
  const [cityOpen, setCityOpen] = useState(false);
  const [citySearch, setCitySearch] = useState('');
  const [heroCities, setHeroCities] = useState([]);
  const [filteredCategories, setFilteredCategories] = useState(null);
  const [platformStats, setPlatformStats] = useState(null);
  const serviceRef = useRef(null);
  const cityRef = useRef(null);

  // Hero rotating images — diverse professional services across categories
  const heroImages = [
    { src: 'https://images.unsplash.com/photo-1522337660859-02fbefca4702?auto=format&fit=crop&q=80&w=1200', alt: 'Servicio profesional de manicura' },
    { src: 'https://customer-assets.emergentagent.com/job_63d07d25-f91a-41d9-abd3-14123baf6a9f/artifacts/0ujkka4x_image.png', alt: 'Barbero recortando cabello con tijeras en barberia moderna' },
    { src: 'https://images.unsplash.com/photo-1580618672591-eb180b1a973f?auto=format&fit=crop&q=80&w=1200', alt: 'Estilista profesional secando y peinando cabello' },
    { src: 'https://images.pexels.com/photos/4270095/pexels-photo-4270095.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=720', alt: 'Dentista mostrando modelo dental a paciente en consulta' },
    { src: 'https://customer-assets.emergentagent.com/job_63d07d25-f91a-41d9-abd3-14123baf6a9f/artifacts/nfemxt0d_image.png', alt: 'Mecánico en overol azul trabajando bajo auto elevado con llave' },
    { src: 'https://images.unsplash.com/photo-1551836022-d5d88e9218df?auto=format&fit=crop&q=80&w=1200', alt: 'Asesora profesional atendiendo a cliente en consulta' },
  ];
  const [heroImageIdx, setHeroImageIdx] = useState(0);

  useEffect(() => {
    const id = setInterval(() => {
      setHeroImageIdx((i) => (i + 1) % heroImages.length);
    }, 4500);
    return () => clearInterval(id);
  }, [heroImages.length]);

  useEffect(() => {
    const handler = (e) => {
      if (serviceRef.current && !serviceRef.current.contains(e.target)) setServiceOpen(false);
      if (cityRef.current && !cityRef.current.contains(e.target)) { setCityOpen(false); setCitySearch(''); }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
    fetch(`${baseUrl}/api/cities?country_code=${countryCode}&with_businesses=true`)
      .then(r => r.ok ? r.json() : [])
      .then(data => setHeroCities(Array.isArray(data) ? data : []))
      .catch(() => setHeroCities([]));
  }, [countryCode]);

  useEffect(() => {
    if (!city) { setFilteredCategories(null); return; }
    const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
    fetch(`${baseUrl}/api/categories?city=${encodeURIComponent(city)}&country_code=${countryCode}`)
      .then(r => r.ok ? r.json() : [])
      .then(data => setFilteredCategories(Array.isArray(data) ? data : []))
      .catch(() => setFilteredCategories(null));
  }, [city, countryCode]);

  useEffect(() => { loadData(); }, [countryCode, city]);

  const loadData = async () => {
    try {
      await utilityAPI.seed().catch(() => {});
      const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
      const [catRes, bizRes, citiesRes, statsRes] = await Promise.all([
        categoriesAPI.getAll({ country_code: countryCode, ...(city ? { city } : {}) }),
        // City-aware featured — when a city is selected we never surface
        // businesses from other cities on the home page.
        businessesAPI.getFeatured(8, countryCode, city || undefined),
        // Phase T: with_businesses=true auto-includes cities from any business
        // registration (even if not in the seed catalog). This way, when a new
        // business signs up in a city we haven't seen before, it appears here.
        fetch(`${baseUrl}/api/cities?country_code=${countryCode}&with_businesses=true`).then(r => r.ok ? r.json() : []).catch(() => []),
        utilityAPI.getPlatformStats().catch(() => ({ data: null })),
      ]);
      setCategories(Array.isArray(catRes.data) ? catRes.data : []);
      setFeaturedBusinesses(Array.isArray(bizRes.data) ? bizRes.data : []);
      setCities(Array.isArray(citiesRes) ? citiesRes : []);
      if (statsRes?.data) setPlatformStats(statsRes.data);
    } catch {
      setCategories([]);
      setFeaturedBusinesses([]);
      setCities([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (selectedCategoryId) {
      params.set('category', selectedCategoryId);
    } else if (searchQuery) {
      params.set('q', searchQuery);
    }
    if (city) params.set('city', city);
    navigate(`/search?${params.toString()}`);
  };

  const countryName = language === 'es' ? country?.name : country?.nameEn;

  const steps = [
    { icon: Search, title: language === 'es' ? 'Busca' : 'Search', desc: language === 'es' ? 'Encuentra el servicio que necesitas cerca de ti' : 'Find the service you need near you' },
    { icon: CheckCircle2, title: language === 'es' ? 'Elige' : 'Choose', desc: language === 'es' ? 'Compara precios, reseñas y disponibilidad' : 'Compare prices, reviews and availability' },
    { icon: CalendarIcon, title: language === 'es' ? 'Reserva' : 'Book', desc: language === 'es' ? 'Selecciona fecha, hora y profesional' : 'Select date, time and professional' },
    { icon: Sparkles, title: language === 'es' ? 'Disfruta' : 'Enjoy', desc: language === 'es' ? 'Acude a tu cita y deja tu reseña' : 'Attend your appointment and leave a review' },
  ];

  return (
    <div className="min-h-screen" data-testid="home-page">
      <JsonLd data={organizationSchema} id="jsonld-org" />
      <JsonLd data={websiteSchema} id="jsonld-site" />
      <HowItWorksModal />

      {/* ═══ Mobile-first experience (redesigned, hidden on md+) ═══ */}
      <div className="md:hidden">
        <MobileHomeExperience
          city={city}
          setCity={setCity}
          cities={cities}
          categories={categories}
          featuredBusinesses={featuredBusinesses}
          onCategoryClick={(cat) => {
            const cityQ = city ? `&city=${encodeURIComponent(city)}` : '';
            if (cat?.id) navigate(`/search?category=${cat.id}${cityQ}`);
            else if (cat?.slug) navigate(`/search?q=${encodeURIComponent(cat.slug)}${cityQ}`);
          }}
          onFeaturedClick={(biz) => navigate(`/business/${biz.slug || biz.id}`)}
          onSearchClick={() => navigate(city ? `/search?city=${encodeURIComponent(city)}` : '/search')}
        />
      </div>

      {/* ═══ Desktop / tablet layout (unchanged) ═══════════════════ */}
      <div className="hidden md:block">
      {/* ═══ Hero ═══════════════════════════════════ */}
      <section className="relative min-h-[88vh] flex items-center overflow-hidden bg-[#fcf7ba]">
        <div className="absolute inset-0 bg-gradient-to-r from-[#fcf7ba] via-[#fcf7ba]/80 to-white" />
        <div className="absolute top-20 left-10 w-72 h-72 bg-[#F05D5E]/10 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#F05D5E]/10 rounded-full blur-3xl" />

        <div className="relative z-10 container-app text-slate-900 py-16 lg:py-20">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-8 items-center">
            {/* LEFT: Text + Search */}
            <div className="lg:col-span-7 space-y-6 text-center lg:text-left animate-fade-in">
              <Badge className="bg-slate-900/5 text-slate-900 border-slate-900/10 text-sm px-4 py-1.5 backdrop-blur-sm inline-flex items-center gap-2">
                <Clock className="h-3.5 w-3.5 text-[#F05D5E]" />
                {language === 'es' ? 'Disponible 24/7' : 'Available 24/7'}
              </Badge>

              <h1 className="text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-heading font-extrabold tracking-tight leading-[0.95]">
                {language === 'es' ? (
                  <span className="inline-flex flex-wrap justify-center lg:justify-start gap-x-4 gap-y-2">
                    <span className="animate-fade-in" style={{ animationDelay: '0.1s' }}>Busca.</span>
                    <span className="animate-fade-in" style={{ animationDelay: '0.3s' }}>Elige.</span>
                    <span className="text-[#F05D5E] animate-fade-in" style={{ animationDelay: '0.5s' }}>Reserva.</span>
                  </span>
                ) : (
                  <span className="inline-flex flex-wrap justify-center lg:justify-start gap-x-4 gap-y-2">
                    <span className="animate-fade-in" style={{ animationDelay: '0.1s' }}>Search.</span>
                    <span className="animate-fade-in" style={{ animationDelay: '0.3s' }}>Choose.</span>
                    <span className="text-[#F05D5E] animate-fade-in" style={{ animationDelay: '0.5s' }}>Book.</span>
                  </span>
                )}
              </h1>

              <p className="text-base sm:text-lg text-slate-700 max-w-2xl lg:mx-0 mx-auto">
                {language === 'es'
                  ? 'Encuentra servicios cerca de ti, compara opciones y agenda fácil en un solo lugar.'
                  : 'Find services near you, compare options and book easily in one place.'}
              </p>

              {/* Search Bar */}
              <form onSubmit={handleSearch} className="mt-8">
                <div className="bg-white/80 backdrop-blur-xl rounded-2xl p-2 max-w-3xl lg:mx-0 mx-auto border border-slate-900/10 shadow-xl">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  {/* City Dropdown */}
                  <div className="relative" ref={cityRef}>
                    <button type="button"
                      onClick={() => { setCityOpen(!cityOpen); setServiceOpen(false); setTimeout(() => { const el = document.getElementById('hero-city-search'); if (el) el.focus(); }, 100); }}
                      className="flex items-center gap-2 w-full h-14 px-4 bg-white rounded-xl text-left"
                      data-testid="search-city-input">
                      <MapPin className="h-5 w-5 text-slate-400 shrink-0" />
                      <span className={`flex-1 text-sm truncate ${city ? 'text-slate-900' : 'text-slate-400'}`}>
                        {city || (language === 'es' ? 'En que ciudad?' : 'Which city?')}
                      </span>
                      <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${cityOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {cityOpen && (
                      <div className="absolute z-50 mt-1 w-full bg-white rounded-xl shadow-xl border max-h-72 overflow-hidden animate-in fade-in-0 zoom-in-95">
                        <div className="p-2 border-b sticky top-0 bg-white">
                          <input id="hero-city-search" type="text"
                            placeholder={language === 'es' ? 'Buscar ciudad...' : 'Search city...'}
                            value={citySearch} onChange={e => setCitySearch(e.target.value)}
                            className="w-full px-3 py-2 text-sm text-slate-900 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F05D5E]/30"
                            data-testid="city-search-input" autoComplete="off" />
                        </div>
                        <div className="overflow-y-auto max-h-52">
                          <button type="button"
                            onClick={() => { setCity(''); setSearchQuery(''); setCityOpen(false); setCitySearch(''); }}
                            className={`flex items-center gap-3 w-full px-4 py-3 text-sm hover:bg-slate-50 transition-colors text-left border-b ${!city ? 'bg-slate-50 font-medium' : ''}`}>
                            <MapPin className="h-4 w-4 text-slate-400" />
                            <span className="text-slate-700">{language === 'es' ? 'Todas las ciudades' : 'All cities'}</span>
                          </button>
                          {heroCities
                            .filter(c => !citySearch || (c.name || '').normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLowerCase().includes(citySearch.normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLowerCase()))
                            .map(c => (
                            <button key={c.slug || c.name} type="button"
                              onClick={() => { setCity(c.name); setCityOpen(false); setCitySearch(''); setSearchQuery(''); }}
                              className={`flex items-center gap-3 w-full px-4 py-3 text-sm hover:bg-slate-50 transition-colors text-left ${city === c.name ? 'bg-slate-50 font-medium' : ''}`}
                              data-testid={`search-city-${c.slug || c.name}`}>
                              <MapPin className="h-4 w-4 text-[#F05D5E]" />
                              <span className="flex-1 text-slate-700">{c.name}</span>
                              <span className="text-xs text-slate-400">{c.business_count} {language === 'es' ? 'negocios' : 'biz'}</span>
                            </button>
                          ))}
                          {heroCities.filter(c => !citySearch || (c.name || '').normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLowerCase().includes(citySearch.normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLowerCase())).length === 0 && (
                            <div className="py-4 text-center text-sm text-slate-400">{language === 'es' ? 'No se encontraron ciudades' : 'No cities found'}</div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  {/* Service Dropdown */}
                  <div className="relative" ref={serviceRef}>
                    <button type="button"
                      onClick={() => { setServiceOpen(!serviceOpen); setCityOpen(false); }}
                      className="flex items-center gap-2 w-full h-14 px-4 bg-white rounded-xl text-left"
                      data-testid="search-service-input">
                      <Search className="h-5 w-5 text-slate-400 shrink-0" />
                      <span className={`flex-1 text-sm truncate ${searchQuery ? 'text-slate-900' : 'text-slate-400'}`}>
                        {searchQuery || (language === 'es' ? 'Busca negocio, servicio o categoría' : 'Search business, service or category')}
                      </span>
                      <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${serviceOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {serviceOpen && (() => {
                      const displayCats = filteredCategories !== null ? filteredCategories : categories;
                      return displayCats.length > 0 ? (
                      <div className="absolute z-50 mt-1 w-full bg-white rounded-xl shadow-xl border max-h-64 overflow-y-auto animate-in fade-in-0 zoom-in-95">
                        <button type="button"
                          onClick={() => { setSearchQuery(''); setSelectedCategoryId(''); setServiceOpen(false); }}
                          className={`flex items-center gap-3 w-full px-4 py-2.5 text-sm hover:bg-slate-50 transition-colors text-left border-b ${!searchQuery ? 'bg-slate-50 font-medium' : ''}`}>
                          <Search className="h-4 w-4 text-slate-400 shrink-0" />
                          <span className="text-slate-700">{language === 'es' ? 'Todos los servicios' : 'All services'}</span>
                        </button>
                        {displayCats.map(cat => {
                          const IconComp = iconMap[cat.icon] || Sparkles;
                          return (
                          <button key={cat.id} type="button"
                            onClick={() => { setSearchQuery(language === 'es' ? cat.name_es : cat.name_en); setSelectedCategoryId(cat.id); setServiceOpen(false); }}
                            className={`flex items-center gap-3 w-full px-4 py-2.5 text-sm hover:bg-slate-50 transition-colors text-left ${searchQuery === (language === 'es' ? cat.name_es : cat.name_en) ? 'bg-slate-50 font-medium' : ''}`}
                            data-testid={`search-cat-${cat.id}`}>
                            <div className="w-7 h-7 rounded-lg bg-[#F05D5E]/10 flex items-center justify-center shrink-0">
                              <IconComp className="h-3.5 w-3.5 text-[#F05D5E]" />
                            </div>
                            <span className="flex-1 text-slate-700 text-left">{language === 'es' ? cat.name_es : cat.name_en}</span>
                            {city && <span className="text-xs text-slate-400 shrink-0">{cat.business_count}</span>}
                          </button>
                          );
                        })}
                      </div>
                      ) : (
                      <div className="absolute z-50 mt-1 w-full bg-white rounded-xl shadow-xl border animate-in fade-in-0 zoom-in-95">
                        <div className="py-4 text-center text-sm text-slate-400">{language === 'es' ? 'No hay servicios en esta ciudad' : 'No services in this city'}</div>
                      </div>
                      );
                    })()}
                  </div>
                  <Button type="submit" className="h-14 btn-coral text-base rounded-xl" data-testid="search-submit-button">
                    {language === 'es' ? 'Buscar' : 'Search'} <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </div>
              </div>
            </form>

            {/* Quick trust indicators */}
            <div className="flex flex-wrap justify-center lg:justify-start gap-4 sm:gap-6 mt-8 pt-6">
              {[
                { icon: Clock, text: language === 'es' ? 'Reserva 24/7' : 'Book 24/7' },
                { icon: CheckCircle2, text: language === 'es' ? 'Confirmacion inmediata' : 'Instant confirmation' },
                { icon: Zap, text: language === 'es' ? 'Sin comisiones ocultas' : 'No hidden fees' },
              ].map(item => (
                <div key={item.text} className="flex items-center gap-2 text-slate-700 text-sm">
                  <item.icon className="h-4 w-4 text-[#F05D5E]" />
                  <span>{item.text}</span>
                </div>
              ))}
            </div>

            {/* Trust badge: Pagos seguros con Stripe */}
            <div className="mt-4 flex flex-wrap items-center justify-center lg:justify-start gap-3" data-testid="trust-stripe-badge">
              <div className="inline-flex items-center gap-2.5 px-3 py-2 rounded-xl bg-white border border-slate-200 shadow-sm">
                <Shield className="h-4 w-4 text-emerald-600 shrink-0" />
                <div className="flex items-center gap-2 text-xs">
                  <span className="text-slate-700 font-medium">
                    {language === 'es' ? 'Pagos seguros con' : 'Secure payments with'}
                  </span>
                  <span className="font-bold text-[#635BFF] tracking-tight">Stripe</span>
                </div>
                <div className="flex items-center gap-1 ml-1 pl-2 border-l border-slate-200">
                  <svg className="h-4 w-auto" viewBox="0 0 32 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-label="Visa">
                    <rect width="32" height="20" rx="2" fill="#1A1F71"/>
                    <text x="16" y="14" textAnchor="middle" fill="white" fontSize="9" fontWeight="bold" fontFamily="Arial">VISA</text>
                  </svg>
                  <svg className="h-4 w-auto" viewBox="0 0 32 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-label="Mastercard">
                    <rect width="32" height="20" rx="2" fill="#000"/>
                    <circle cx="13" cy="10" r="5" fill="#EB001B" opacity="0.9"/>
                    <circle cx="19" cy="10" r="5" fill="#F79E1B" opacity="0.9"/>
                  </svg>
                  <svg className="h-4 w-auto" viewBox="0 0 32 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-label="American Express">
                    <rect width="32" height="20" rx="2" fill="#2E77BB"/>
                    <text x="16" y="14" textAnchor="middle" fill="white" fontSize="6" fontWeight="bold" fontFamily="Arial">AMEX</text>
                  </svg>
                </div>
              </div>
            </div>
          </div>

            {/* RIGHT: Image + Floating cards */}
            <div className="lg:col-span-5 relative hidden lg:block animate-fade-in" style={{ animationDelay: '0.6s' }}>
              <div className="relative">
                {/* Main image — rotating carousel of professional services */}
                <div className="relative rounded-3xl overflow-hidden shadow-2xl border border-slate-900/10 aspect-[4/5]">
                  {heroImages.map((img, i) => (
                    <img
                      key={img.src}
                      src={img.src}
                      alt={img.alt}
                      className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ease-in-out ${i === heroImageIdx ? 'opacity-100' : 'opacity-0'}`}
                      loading={i === 0 ? 'eager' : 'lazy'}
                      data-testid={`hero-image-${i}`}
                    />
                  ))}
                </div>

                {/* CTA: Registrar negocio - LLAMATIVO (bottom-right) */}
                <button
                  type="button"
                  onClick={() => navigate('/for-business')}
                  className="absolute -bottom-8 -right-8 bg-gradient-to-br from-[#F05D5E] to-[#d94748] hover:from-[#d94748] hover:to-[#c93d3e] rounded-3xl shadow-2xl p-5 animate-fade-in w-[300px] text-left transition-all duration-300 hover:scale-105 hover:-translate-y-1 hover:shadow-[0_25px_60px_-10px_rgba(240,93,94,0.6)] group ring-4 ring-white"
                  style={{ animationDelay: '0.9s', boxShadow: '0 20px 40px -10px rgba(240, 93, 94, 0.4)' }}
                  data-testid="hero-register-business-cta"
                >
                  {/* Pulse effect */}
                  <span className="absolute inset-0 rounded-3xl bg-[#F05D5E] opacity-30 animate-ping pointer-events-none" />

                  <div className="relative z-10">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="px-2.5 py-1 rounded-full bg-white text-[#F05D5E] text-[10px] font-extrabold uppercase tracking-wider shadow-md">
                        {language === 'es' ? '🎉 Gratis 30 días' : '🎉 Free 30 days'}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="h-14 w-14 rounded-2xl bg-white flex items-center justify-center shrink-0 shadow-lg">
                        <Building2 className="h-7 w-7 text-[#F05D5E]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-lg font-extrabold text-white leading-tight">
                          {language === 'es' ? '¿Tienes un negocio?' : 'Got a business?'}
                        </div>
                        <div className="text-sm text-white/95 font-bold mt-1 flex items-center gap-1.5 group-hover:gap-3 transition-all">
                          {language === 'es' ? 'Regístralo aquí' : 'Register it here'}
                          <ArrowRight className="h-4 w-4" />
                        </div>
                      </div>
                    </div>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-5 h-8 border-2 border-slate-900/30 rounded-full flex justify-center pt-1.5">
            <div className="w-1 h-1.5 bg-slate-900/40 rounded-full" />
          </div>
        </div>
      </section>

      {/* ═══ How It Works ═════════════════════════════ */}
      <section className="section-padding bg-background" data-testid="how-it-works-section">
        <div className="container-app">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-heading font-bold tracking-tight">
              {language === 'es' ? 'Como funciona?' : 'How does it work?'}
            </h2>
            <p className="text-muted-foreground mt-2 text-sm">
              {language === 'es' ? 'Reserva en 4 simples pasos' : 'Book in 4 simple steps'}
            </p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
            {steps.map((step, index) => (
              <div key={index} className="relative text-center group">
                {index < steps.length - 1 && (
                  <div className="hidden lg:block absolute top-10 left-[60%] w-[80%] h-px bg-gradient-to-r from-[#F05D5E]/40 to-transparent" />
                )}
                <div className="relative inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-[#F05D5E]/10 text-[#F05D5E] mb-4 group-hover:bg-[#F05D5E] group-hover:text-white transition-all duration-300">
                  <step.icon className="h-8 w-8" />
                  <span className="absolute -top-1.5 -right-1.5 w-6 h-6 rounded-full bg-[#F05D5E] text-white text-xs font-bold flex items-center justify-center">
                    {index + 1}
                  </span>
                </div>
                <h3 className="font-heading font-bold text-base mb-1">{step.title}</h3>
                <p className="text-muted-foreground text-xs sm:text-sm leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ Categories ═══════════════════════════════ */}
      <section className="section-padding bg-muted/30" data-testid="categories-section">
        <div className="container-app">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-heading font-bold tracking-tight">
              {language === 'es' ? 'Explora por categoria' : 'Explore by category'}
            </h2>
            <p className="text-muted-foreground mt-2 text-sm">
              {language === 'es' ? 'Encuentra exactamente lo que necesitas' : 'Find exactly what you need'}
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
            {(Array.isArray(categories) ? categories : []).map((category, index) => {
              const IconComponent = iconMap[category.icon] || Sparkles;
              return (
                <Card key={category.id}
                  className="group cursor-pointer overflow-hidden border-0 shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
                  onClick={() => navigate(city ? `/search?category=${category.id}&city=${encodeURIComponent(city)}` : `/search?category=${category.id}`)}
                  data-testid={`category-card-${category.slug}`}>
                  <div className="relative aspect-[4/3] overflow-hidden">
                    <img
                      src={category.image_url || 'https://images.unsplash.com/photo-1560066984-138dadb4c035?w=400'}
                      alt={language === 'es' ? category.name_es : category.name_en}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-slate-900/20 to-transparent" />
                    <div className="absolute bottom-3 left-3 right-3">
                      <div className="flex items-center gap-2 text-white">
                        <div className="p-1.5 rounded-lg bg-[#F05D5E]">
                          <IconComponent className="h-4 w-4" />
                        </div>
                        <h3 className="font-heading font-bold text-sm sm:text-base">
                          {language === 'es' ? category.name_es : category.name_en}
                        </h3>
                      </div>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* ═══ Featured Businesses ══════════════════════ */}
      {featuredBusinesses.length > 0 && (
        <section className="section-padding bg-background" data-testid="featured-section">
          <div className="container-app">
            <div className="flex items-end justify-between mb-8">
              <div>
                <h2 className="text-2xl sm:text-3xl font-heading font-bold tracking-tight">
                  {language === 'es' ? 'Negocios destacados' : 'Featured businesses'}
                </h2>
                <p className="text-muted-foreground mt-1 text-sm">
                  {language === 'es' ? 'Los mejor valorados por nuestros usuarios' : 'Top rated by our users'}
                </p>
              </div>
              <Button variant="ghost" onClick={() => navigate('/search')} className="hidden md:flex text-[#F05D5E] hover:text-[#D94A4B]" data-testid="view-all-featured">
                {language === 'es' ? 'Ver todos' : 'View all'} <ArrowRight className="ml-1 h-4 w-4" />
              </Button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {featuredBusinesses.map(business => (
                <BusinessCard key={business.id} business={business} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ═══ Testimonials ═════════════════════════════ */}
      {/* Removed — esperando testimonios reales de beta users */}

      {/* ═══ Business CTA ═════════════════════════════ */}
      <section className="section-padding bg-[#fcf7ba] text-slate-900 relative overflow-hidden" data-testid="business-cta-section">
        <div className="absolute top-10 right-10 w-72 h-72 bg-[#F05D5E]/10 rounded-full blur-3xl" />
        <div className="absolute bottom-10 left-10 w-96 h-96 bg-[#F05D5E]/5 rounded-full blur-3xl" />
        <div className="container-app relative z-10">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div className="space-y-5">
              <Badge className="bg-[#F05D5E]/15 text-[#F05D5E] border-[#F05D5E]/30">
                {language === 'es' ? 'Tienes un negocio?' : 'Have a business?'}
              </Badge>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-heading font-bold tracking-tight leading-tight">
                {language === 'es'
                  ? 'Haz crecer tu negocio con Bookvia'
                  : 'Grow your business with Bookvia'}
              </h2>
              <p className="text-slate-700 leading-relaxed">
                {language === 'es'
                  ? 'Gestiona tus citas, reduce cancelaciones y haz crecer tu negocio con la plataforma de reservas más completa de México.'
                  : 'Manage appointments, reduce cancellations and grow your business with the most complete booking platform in Mexico.'}
              </p>
              <div className="grid grid-cols-2 gap-3 pt-2">
                {[
                  { icon: CalendarIcon, text: language === 'es' ? 'Agenda inteligente' : 'Smart scheduling' },
                  { icon: Users, text: language === 'es' ? 'Gestion de equipo' : 'Team management' },
                  { icon: Shield, text: language === 'es' ? 'Pagos seguros' : 'Secure payments' },
                  { icon: Zap, text: language === 'es' ? 'Recordatorios automaticos' : 'Auto reminders' },
                ].map(feat => (
                  <div key={feat.text} className="flex items-center gap-2">
                    <feat.icon className="h-4 w-4 text-[#F05D5E] shrink-0" />
                    <span className="text-sm text-slate-700">{feat.text}</span>
                  </div>
                ))}
              </div>
              <div className="flex flex-col sm:flex-row gap-3 pt-2">
                <Button size="lg" className="btn-coral" onClick={() => navigate('/for-business')} data-testid="register-business-cta">
                  {language === 'es' ? 'Conocer mas' : 'Learn more'}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </div>
            </div>
            <div className="relative hidden lg:block">
              <img src="https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?auto=format&fit=crop&q=80&w=800" alt="" className="rounded-2xl shadow-2xl" />
            </div>
          </div>
        </div>
      </section>

      {/* ═══ Popular Cities ═════════════════════════════ */}
      {cities.filter(c => c.business_count > 0).length > 0 && (
        <section className="section-padding bg-muted/30" data-testid="cities-section">
          <div className="container-app">
            <div className="text-center mb-8">
              <h2 className="text-2xl sm:text-3xl font-heading font-bold tracking-tight">
                {language === 'es' ? 'Ciudades disponibles' : 'Available cities'}
              </h2>
              <p className="text-muted-foreground mt-2 text-sm">
                {language === 'es' ? 'Descubre los mejores servicios en tu ciudad' : 'Discover the best services in your city'}
              </p>
            </div>
            <div className="flex flex-wrap justify-center gap-3">
              {cities.filter(c => c.business_count > 0).slice(0, 8).map(c => (
                <Card key={c.slug || c.name}
                  className="group cursor-pointer border-0 shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
                  onClick={() => navigate(`/search?city=${c.name}`)}
                  data-testid={`city-card-${c.slug || c.name}`}>
                  <div className="px-5 py-3 flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-[#F05D5E]/10 flex items-center justify-center shrink-0">
                      <MapPin className="h-4 w-4 text-[#F05D5E]" />
                    </div>
                    <div>
                      <h3 className="font-heading font-bold text-sm">{c.name}</h3>
                      <p className="text-xs text-muted-foreground">{c.business_count} {language === 'es' ? 'negocios' : 'businesses'}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ═══ Trust Badges ═════════════════════════════ */}
      <section className="py-12 bg-background border-y" data-testid="trust-section">
        <div className="container-app">
          <div className="flex flex-wrap justify-center gap-8 sm:gap-16">
            {[
              { icon: Shield, label: language === 'es' ? 'Pagos seguros con Stripe' : 'Secure Stripe payments' },
              { icon: Star, label: language === 'es' ? 'Reseñas verificadas' : 'Verified reviews' },
              { icon: Clock, label: language === 'es' ? 'Reserva 24/7' : 'Book 24/7' },
              { icon: Building2, label: language === 'es' ? 'Negocios verificados' : 'Verified businesses' },
            ].map(item => (
              <div key={item.label} className="flex items-center gap-2 text-muted-foreground">
                <item.icon className="h-5 w-5 text-[#F05D5E]" />
                <span className="text-sm font-medium">{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
      </div>
    </div>
  );
}
